import { IS_WEB } from 'constants/platform'
import * as Haptics from 'expo-haptics'
import React, { useLayoutEffect, useMemo } from 'react'
import { StyleSheet } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated'
import Toast from 'react-native-toast-message'

import { Box } from 'widgets/Box'
import { Card } from 'widgets/Card'
import { Icon } from 'widgets/Icon'
import { Pressable } from 'widgets/Pressable'
import { Text } from 'widgets/Text'

import { ThemeColor } from 'utils/themes/types'

interface ToastWrapperProps {
  type?: 'success' | 'info' | 'warn' | 'error'
  title?: string
  subtitle?: string
  backgroundColor?: ThemeColor
  textColor?: ThemeColor
  onPress?: () => void
  noWrapSubtitle?: boolean
  v2?: boolean
  action?: () => void
  actionText?: string
  isImportant?: boolean
  id: string
  emoji?: string
  icon?: React.ReactNode
}

const toastWrapperStyles = StyleSheet.create({
  pressable: { paddingTop: 0, maxWidth: '100%' },
  row: { maxWidth: '100%' },
  textContainer: { maxWidth: '90%' },
})

const ToastWrapper = ({
  type,
  title,
  subtitle,
  backgroundColor = 'secondary',
  textColor = 'text',
  onPress,
  noWrapSubtitle = false,
  v2,
  action,
  actionText,
  isImportant = false,
  id,
  emoji,
  icon,
}: ToastWrapperProps) => {
  const rotation = useSharedValue(0)

  const animatedStyle = useAnimatedStyle(() => {
    return {
      width: '100%',
      transform: [{ rotateZ: `${rotation.value}deg` }],
    }
  })

  useLayoutEffect(() => {
    if (v2) {
      // Do shake animation after slide-up animation is complete.
      setTimeout(() => {
        if (action && rotation.value === 0) {
          rotation.value = withSequence(
            withTiming(-2, { duration: 50 }),
            withRepeat(withTiming(0, { duration: 100 }), 3, true),
            withTiming(0, { duration: 50 }),
          )
          if (!IS_WEB) {
            Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light)
          }
        } else if (isImportant && !IS_WEB) {
          if (type === 'success') {
            Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success)
          } else if (type === 'warn') {
            Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning)
          }
        }
      }, 250)
    }
  }, [action, isImportant, rotation, type, v2, id])

  const emojiToUse = useMemo(() => {
    if (emoji) return emoji
    if (type === 'success') return '✅'
    if (type === 'warn') return '⚡'
    if (type === 'error') return '‼️'

    return ''
  }, [emoji, type])

  return (
    <Animated.View style={animatedStyle}>
      <Pressable onPress={onPress} style={toastWrapperStyles.pressable}>
        <Card
          backgroundColor={!v2 ? backgroundColor : 'black'}
          borderRadius={v2 ? 'sm' : undefined}
          padding={v2 ? 'xs' : undefined}
          margin={v2 ? 'xs' : undefined}
        >
          <Box
            testID='ToastWrapperRow'
            display='flex'
            flexDirection='row'
            justifyContent='space-between'
            align='center'
            padding={{ horizontal: 'md', vertical: 'sm' }}
            style={toastWrapperStyles.row}
          >
            {icon || null}
            <Box style={toastWrapperStyles.textContainer}>
              {title !== '' && (
                <Box padding={{ bottom: v2 && !IS_WEB ? 'xs' : undefined }}>
                  <Text color={textColor} selectable={false}>
                    {v2 && !action && `${emojiToUse} ${title}`}
                    {(!v2 || action) && `${title}`}
                  </Text>
                </Box>
              )}
              <Text
                color={textColor}
                bold={!!v2}
                fontSize={!icon ? 'sm' : 'xs'}
                selectable={false}
                numberOfLines={noWrapSubtitle ? 1 : undefined}
              >
                {emoji && `${emoji} `}
                {subtitle}
              </Text>
            </Box>
            {!action ? (
              <Pressable onPress={() => Toast.hide()}>
                <Box padding={{ left: 'md', vertical: 'sm' }}>
                  <Icon name='x' size={20} color={textColor} />
                </Box>
              </Pressable>
            ) : (
              <Pressable onPress={action}>
                <Box
                  padding={{
                    left: v2 && !IS_WEB ? 'md' : undefined,
                    vertical: v2 && !IS_WEB ? 'sm' : undefined,
                  }}
                >
                  <Text color='primary' bold>
                    {actionText}
                  </Text>
                </Box>
              </Pressable>
            )}
          </Box>
        </Card>
      </Pressable>
    </Animated.View>
  )
}

const toastConfig = {
  error: ({ text1, text2, props, ...rest }: any) => (
    <ToastWrapper
      title={text1}
      subtitle={text2}
      type='error'
      backgroundColor='error'
      textColor={!props.v2 ? 'text' : 'textInverted'}
      v2={props.v2}
      action={props.action}
      actionText={props.actionText}
      isImportant={props.isImportant}
      id={props.id}
      emoji={props.emoji}
      icon={props.icon}
      {...rest}
    />
  ),

  warn: ({ text1, text2, props, ...rest }: any) => (
    <ToastWrapper
      title={text1}
      subtitle={text2}
      type='warning'
      backgroundColor='warn'
      textColor={!props.v2 ? 'text' : 'textInverted'}
      v2={props.v2}
      action={props.action}
      actionText={props.actionText}
      isImportant={props.isImportant}
      id={props.id}
      emoji={props.emoji}
      icon={props.icon}
      {...rest}
    />
  ),

  info: ({ text1, text2, props, ...rest }: any) => (
    <ToastWrapper
      title={text1}
      subtitle={text2}
      type='info'
      backgroundColor='primary'
      textColor={!props.v2 ? 'text' : 'textInverted'}
      v2={props.v2}
      action={props.action}
      actionText={props.actionText}
      isImportant={props.isImportant}
      id={props.id}
      emoji={props.emoji}
      icon={props.icon}
      {...rest}
    />
  ),

  success: ({ text1, text2, props, ...rest }: any) => (
    <ToastWrapper
      title={text1}
      subtitle={text2}
      noWrapSubtitle={props.noWrapSubtitle}
      type='success'
      backgroundColor='secondary'
      textColor={!props.v2 ? 'text' : 'textInverted'}
      v2={props.v2}
      action={props.action}
      actionText={props.actionText}
      isImportant={props.isImportant}
      id={props.id}
      emoji={props.emoji}
      icon={props.icon}
      {...rest}
    />
  ),
}

export const InternalToast = () => {
  return <Toast config={toastConfig} bottomOffset={!IS_WEB ? 88 : 64} />
}
